import React, { useState, useEffect } from 'react';
import Button from './ui/Button';
import Card from './ui/Card';
import { theme } from '../styles/theme';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Detectar sistema operacional
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    setIsIOS(/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream);
    setIsAndroid(/android/i.test(userAgent));

    // Verificar se já está instalado
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Verificar se o app pode ser instalado
    const checkInstallability = () => {
      // Para Android/Chrome
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        window.addEventListener('beforeinstallprompt', (e) => {
          e.preventDefault();
          setDeferredPrompt(e);
          setShowInstallPrompt(true);
        });
      }

      // Para iOS Safari
      if (isIOS && window.navigator.standalone === false) {
        setShowInstallPrompt(true);
      }
    };

    // Mostrar o prompt imediatamente para ser mais obrigatório
    const timer = setTimeout(() => {
      checkInstallability();
    }, 1000);

    return () => clearTimeout(timer);
  }, [isIOS]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setShowInstallPrompt(false);
        setIsInstalled(true);
      }
    }
  };

  const handleClose = () => {
    setShowInstallPrompt(false);
    // Mostrar novamente após 10 segundos (mais persistente)
    setTimeout(() => {
      if (!isInstalled) {
        setShowInstallPrompt(true);
      }
    }, 10000);
  };

  if (isInstalled || !showInstallPrompt) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: theme.spacing.xl,
    }}>
      <Card 
        variant="elevated" 
        padding="xl" 
        style={{ 
          maxWidth: '500px', 
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          textAlign: 'center',
          position: 'relative',
        }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: theme.spacing.md,
            right: theme.spacing.md,
            background: 'none',
            border: 'none',
            fontSize: theme.typography.fontSize.xl,
            cursor: 'pointer',
            color: theme.colors.gray[500],
            padding: theme.spacing.xs,
            borderRadius: theme.borderRadius.sm,
            transition: theme.transitions.normal,
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.colors.gray[100];
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
        >
          ✕
        </button>

        {/* Header */}
        <div style={{ marginBottom: theme.spacing.xl }}>
          <div style={{
            fontSize: theme.typography.fontSize['4xl'],
            marginBottom: theme.spacing.md,
          }}>
            📱
          </div>
          <h2 style={{
            fontSize: theme.typography.fontSize['2xl'],
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.gray[800],
            marginBottom: theme.spacing.md,
          }}>
            Instalar Draiva App
          </h2>
          <p style={{
            fontSize: theme.typography.fontSize.base,
            color: theme.colors.gray[600],
            lineHeight: 1.6,
          }}>
            Instale o app para ter acesso offline e uma experiência melhor!
          </p>
        </div>

        {/* Android Installation */}
        {isAndroid && deferredPrompt && (
          <div style={{ marginBottom: theme.spacing.lg }}>
            <div style={{
              backgroundColor: `${theme.colors.primary}10`,
              border: `1px solid ${theme.colors.primary}30`,
              borderRadius: theme.borderRadius.lg,
              padding: theme.spacing.lg,
              marginBottom: theme.spacing.md,
            }}>
              <h3 style={{
                fontSize: theme.typography.fontSize.lg,
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.primary,
                marginBottom: theme.spacing.sm,
              }}>
                📱 Para Android
              </h3>
              <p style={{
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.gray[600],
                marginBottom: theme.spacing.md,
              }}>
                Clique no botão abaixo para instalar o app no seu dispositivo
              </p>
              <Button
                onClick={handleInstallClick}
                variant="primary"
                fullWidth
                size="lg"
              >
                🚀 Instalar App
              </Button>
            </div>
          </div>
        )}

        {/* iOS Installation */}
        {isIOS && (
          <div style={{ marginBottom: theme.spacing.lg }}>
            <div style={{
              backgroundColor: `${theme.colors.secondary}10`,
              border: `1px solid ${theme.colors.secondary}30`,
              borderRadius: theme.borderRadius.lg,
              padding: theme.spacing.lg,
              marginBottom: theme.spacing.md,
            }}>
              <h3 style={{
                fontSize: theme.typography.fontSize.lg,
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.secondary,
                marginBottom: theme.spacing.sm,
              }}>
                🍎 Para iPhone/iPad
              </h3>
              <div style={{
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.gray[600],
                textAlign: 'left',
                lineHeight: 1.6,
              }}>
                <p style={{ marginBottom: theme.spacing.sm }}>
                  <strong>Passo 1:</strong> Toque no botão de compartilhar <span style={{ fontSize: '1.2em' }}>📤</span> na barra de endereços
                </p>
                <p style={{ marginBottom: theme.spacing.sm }}>
                  <strong>Passo 2:</strong> Role para baixo e toque em "Adicionar à Tela de Início"
                </p>
                <p style={{ marginBottom: theme.spacing.sm }}>
                  <strong>Passo 3:</strong> Toque em "Adicionar" para confirmar
                </p>
                <p>
                  <strong>Passo 4:</strong> O app aparecerá na sua tela de início
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Desktop Installation */}
        {!isAndroid && !isIOS && deferredPrompt && (
          <div style={{ marginBottom: theme.spacing.lg }}>
            <div style={{
              backgroundColor: `${theme.colors.info}10`,
              border: `1px solid ${theme.colors.info}30`,
              borderRadius: theme.borderRadius.lg,
              padding: theme.spacing.lg,
              marginBottom: theme.spacing.md,
            }}>
              <h3 style={{
                fontSize: theme.typography.fontSize.lg,
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.info,
                marginBottom: theme.spacing.sm,
              }}>
                💻 Para Desktop
              </h3>
              <p style={{
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.gray[600],
                marginBottom: theme.spacing.md,
              }}>
                Clique no botão abaixo para instalar o app no seu computador
              </p>
              <Button
                onClick={handleInstallClick}
                variant="primary"
                fullWidth
                size="lg"
              >
                🚀 Instalar App
              </Button>
            </div>
          </div>
        )}

        {/* Benefits */}
        <div style={{
          backgroundColor: theme.colors.gray[50],
          borderRadius: theme.borderRadius.lg,
          padding: theme.spacing.lg,
        }}>
          <h4 style={{
            fontSize: theme.typography.fontSize.base,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.gray[800],
            marginBottom: theme.spacing.md,
          }}>
            ✨ Benefícios da Instalação:
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: theme.spacing.md,
            fontSize: theme.typography.fontSize.xs,
            color: theme.colors.gray[600],
          }}>
            <div>📱 Acesso offline</div>
            <div>⚡ Carregamento mais rápido</div>
            <div>🔔 Notificações push</div>
            <div>🎯 Experiência nativa</div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: theme.spacing.lg,
          paddingTop: theme.spacing.lg,
          borderTop: `1px solid ${theme.colors.gray[200]}`,
        }}>
          <p style={{
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.gray[500],
            marginBottom: theme.spacing.md,
          }}>
            Você pode instalar o app a qualquer momento através do menu do navegador
          </p>
          <Button
            onClick={handleClose}
            variant="outline"
            size="sm"
          >
            Agora não
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default InstallPWA; 